import React from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Users, Heart, Zap, Sparkles, TrendingUp, ArrowRight } from 'lucide-react';
import ConfettiEffect from './effects/ConfettiEffect';

interface EventOutcomeProps {
  choiceText: string;
  outcome: {
    funds?: number;
    fanbase?: number;
    reputation?: number;
    mentalHealth?: number;
    energy?: number;
    creativity?: number;
  };
  unlocks?: string[];
  onContinue: () => void;
}

const EventOutcome: React.FC<EventOutcomeProps> = ({ choiceText, outcome, unlocks, onContinue }) => {
  const hasPositiveOutcome = Object.values(outcome).some(v => typeof v === 'number' && v > 0);

  const getIcon = (key: string) => {
    switch (key) {
      case 'funds': return <DollarSign className="w-5 h-5" />;
      case 'fanbase': return <Users className="w-5 h-5" />;
      case 'reputation': return <Sparkles className="w-5 h-5" />;
      case 'mentalHealth': return <Heart className="w-5 h-5" />;
      case 'energy': return <Zap className="w-5 h-5" />;
      case 'creativity': return <TrendingUp className="w-5 h-5" />;
      default: return null;
    }
  };

  const formatValue = (key: string, value: number) => {
    const prefix = value > 0 ? '+' : '';
    switch (key) {
      case 'funds': return `${prefix}$${value}`;
      case 'fanbase': return `${prefix}${value} Fans`;
      case 'reputation': return `${prefix}${value} Reputation`;
      case 'mentalHealth': return `${prefix}${value}% Mental Health`;
      case 'energy': return `${prefix}${value}% Energy`;
      case 'creativity': return `${prefix}${value}% Creativity`;
      default: return `${prefix}${value}`;
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {hasPositiveOutcome && <ConfettiEffect />}
      
      <motion.div
        className="relative max-w-md w-full bg-gradient-to-br from-card via-card to-card/80 border-2 border-primary/30 rounded-xl shadow-2xl overflow-hidden"
        initial={{ scale: 0.5, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        exit={{ scale: 0.5, rotate: 10 }}
        transition={{ type: 'spring', duration: 0.5 }}
      >
        {/* Header */}
        <div className="p-6 pb-4 bg-gradient-to-r from-primary/20 to-purple-500/20 border-b border-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">✨</span>
            <h3 className="text-lg font-semibold text-muted-foreground">You chose...</h3>
          </div>
          <p className="text-xl font-bold">{choiceText}</p>
        </div>

        {/* Outcomes */}
        <div className="p-6 space-y-3">
          {Object.entries(outcome).map(([key, value], index) => {
            if (typeof value !== 'number') return null;
            const isPositive = value > 0;
            
            return (
              <motion.div
                key={key}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  isPositive 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-red-500/10 border-red-500/30'
                }`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className={isPositive ? 'text-green-400' : 'text-red-400'}>
                  {getIcon(key)}
                </div>
                <span className={`text-lg font-bold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                  {formatValue(key, value)}
                </span>
              </motion.div>
            );
          })}

          {/* Unlocks */}
          {unlocks && unlocks.length > 0 && (
            <motion.div
              className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center gap-2 text-purple-400 mb-1">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-semibold">Unlocked!</span>
              </div>
              {unlocks.map((unlock, i) => (
                <div key={i} className="text-sm text-foreground">{unlock}</div>
              ))}
            </motion.div>
          )}
        </div>

        {/* Continue Button */}
        <div className="p-6 pt-0">
          <motion.button
            className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold flex items-center justify-center gap-2"
            onClick={onContinue}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Continue <ArrowRight className="w-5 h-5" />
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EventOutcome;
